import { Button, Input, Modal, Table } from "antd";
import { CREATE_jwel } from "../../../Config/Config";
import axios from "axios";
import { useRef, useState } from "react";
import { HiOutlineDocumentReport } from "react-icons/hi";
import { FiRefreshCw, FiX, FiSearch } from "react-icons/fi";

// A handful of hover / nth-child / antd-internal overrides can't be expressed
// as inline style props in React, so they live in this one scoped <style> tag
// instead of a separate .css file. Everything else below is inline.
const scopedCss = `
  .cbid-modal .ant-modal-content { padding: 0; overflow: hidden; border-radius: 14px; }
  .cbid-modal .ant-modal-header { margin: 0; padding: 0; border-bottom: none; background: transparent; }
  .cbid-modal .ant-modal-body { padding: 0; }
  .cbid-icon-btn:hover { background: rgba(255,255,255,0.24) !important; }
  .cbid-search-pill:focus-within { border-color: #234a75 !important; box-shadow: 0 0 0 3px rgba(35,74,117,0.12); }
  .cbid-submit-btn:hover, .cbid-submit-btn:focus { background: #1c3a5e !important; color: #fff !important; }
  .cbid-table .ant-table-thead > tr > th { background: #eef2f7; color: #43536b; font-size: 12.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; border-bottom: 1px solid #e2e8f0; padding: 10px 14px; }
  .cbid-table .ant-table-tbody > tr > td { padding: 10px 14px; font-size: 13.5px; color: #33415c; border-bottom: 1px solid #f0f3f7; }
  .cbid-table .ant-table-tbody > tr:nth-child(even) > td { background: #fafbfd; }
  .cbid-table .ant-table-tbody > tr:hover > td { background: #eef4fa !important; }
`;

const styles = {
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    padding: "18px 24px",
    background: "linear-gradient(135deg, #1c3a5e 0%, #234a75 100%)",
    position: "relative",
    overflow: "hidden",
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 14, zIndex: 1 },
  headerIcon: {
    width: 38,
    height: 38,
    borderRadius: 10,
    background: "rgba(255,255,255,0.14)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    flexShrink: 0,
  },
  headerText: { display: "flex", flexDirection: "column", gap: 2 },
  headerTitle: { color: "#fff", fontSize: 16, fontWeight: 600, lineHeight: 1.2 },
  headerSubtitle: {
    color: "rgba(255,255,255,0.65)",
    fontSize: 12.5,
    lineHeight: 1.2,
  },
  headerActions: { display: "flex", alignItems: "center", gap: 8, zIndex: 1 },
  iconBtn: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: "none",
    background: "rgba(255,255,255,0.12)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  body: { padding: "18px 24px 22px", background: "#f7f9fb" },
  filters: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  searchPill: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 999,
    padding: "0 14px",
    height: 40,
    minWidth: 240,
  },
  searchIcon: { color: "#94a3b8", flexShrink: 0 },
  searchInput: { padding: 0, fontWeight: 600, fontSize: 14, color: "#1e293b" },
  submitBtn: {
    height: 40,
    padding: "0 22px",
    borderRadius: 999,
    border: "none",
    background: "#234a75",
    color: "#fff",
    fontWeight: 600,
    boxShadow: "none",
  },
  totalStat: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginLeft: "auto",
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 999,
    padding: "6px 8px 6px 18px",
    height: 40,
  },
  totalLabel: { fontSize: 12.5, fontWeight: 600, color: "#64748b" },
  totalValue: {
    fontSize: 17,
    fontWeight: 700,
    color: "#1c3a5e",
    background: "#eaf1f8",
    borderRadius: 999,
    padding: "5px 16px",
    fontVariantNumeric: "tabular-nums",
  },
  tableCard: {
    background: "#fff",
    border: "1px solid #e9edf2",
    borderRadius: 12,
    overflow: "hidden",
  },
  sno: { color: "#94a3b8", fontWeight: 600 },
  billnoChip: {
    display: "inline-block",
    fontWeight: 700,
    fontSize: 13.5,
    color: "#1c3a5e",
    background: "#eaf1f8",
    borderRadius: 6,
    padding: "2px 10px",
  },
  num: { fontVariantNumeric: "tabular-nums", fontWeight: 500 },
  numDiscount: { color: "#b45309" },
  numNet: { fontWeight: 700, color: "#1c3a5e" },
  muted: { color: "#a0aec0" },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    padding: "24px 0",
  },
  emptyTitle: { fontWeight: 600, color: "#475569", fontSize: 14 },
  emptySub: { color: "#94a3b8", fontSize: 12.5 },
};

const CustomerBillInfoDialog = ({
  mobileOpen,
  setMobileOpen,
  mobile,
  setMobile,
  smCodeRef,
}) => {
  const searchInputRef = useRef(null);
  const submitBillNoRef = useRef(null);
  const [billData, setBillData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const tenantName = localStorage.getItem("tenantName");

  const billNoAPI = async () => {
    if (!/^\d{10}$/.test(mobile)) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhereandOrder?tableName=CREDIT_BILLMAST&where=PHONENO='${mobile}'&order=BILLNO`,
        {
          headers: {
            tenantName: tenantName,
          },
        }
      );

      const data = response.data;
      setSearched(true);

      if (Array.isArray(data) && data.length > 0) {
        setBillData(data);
        const totalAmt = data.reduce(
          (acc, item) => acc + (Number(item.NETAMT) || 0),
          0
        );
        setTotalAmount(totalAmt);
      } else {
        setBillData([]);
        setTotalAmount(0);
      }
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns3 = [
    {
      title: "S.No",
      dataIndex: "sno",
      width: 56,
      key: "sno",
      align: "center",
      render: (_, __, index) => <span style={styles.sno}>{index + 1}</span>,
    },
    {
      title: "Invoice No",
      dataIndex: "billno",
      key: "billno",
      render: (text) => <span style={styles.billnoChip}>{text}</span>,
    },
    {
      title: "Invoice Date",
      dataIndex: "billdate",
      key: "billdate",
      render: (text) =>
        text ? (
          new Date(text).toLocaleDateString("en-GB").replace(/\//g, "-")
        ) : (
          <span style={styles.muted}>N/A</span>
        ),
    },
    {
      title: "Total Qty",
      dataIndex: "TOTQTY",
      key: "TOTQTY",
      align: "right",
      render: (text) => <span style={styles.num}>{text ?? 0}</span>,
    },
    {
      title: "Gross Amt",
      dataIndex: "GROSSAMT",
      key: "GROSSAMT",
      align: "right",
      render: (text) => (
        <span style={styles.num}>{Number(text || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Discount",
      dataIndex: "discount",
      key: "discount",
      align: "right",
      render: (text) => (
        <span style={{ ...styles.num, ...styles.numDiscount }}>
          {Number(text || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Net Amt",
      dataIndex: "NETAMT",
      key: "NETAMT",
      align: "right",
      render: (text) => (
        <span style={{ ...styles.num, ...styles.numNet }}>
          {Number(text || 0).toFixed(2)}
        </span>
      ),
    },
  ];

  const handleKeyDown = (e, nextRef) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextRef?.current) {
        nextRef.current.focus();
      } else {
        billNoAPI();
      }
    }
  };

  return (
    <Modal
      open={mobileOpen}
      onCancel={() => {
        setMobileOpen(false);
        smCodeRef.current?.focus();
      }}
      footer={null}
      width={1000}
      closeIcon={null}
      className="cbid-modal"
      maskStyle={{
        backdropFilter: "blur(8px)",
        backgroundColor: "rgba(10, 20, 35, 0.35)",
      }}
      title={
        <div style={styles.header}>
          <style>{scopedCss}</style>
          <div style={styles.headerLeft}>
            <div style={styles.headerIcon}>
              <HiOutlineDocumentReport size={20} />
            </div>
            <div style={styles.headerText}>
              <span style={styles.headerTitle}>Customer Bill Details</span>
              <span style={styles.headerSubtitle}>
                {searched
                  ? `${billData.length} invoice${billData.length === 1 ? "" : "s"
                  } found`
                  : "Search by mobile number to view purchase history"}
              </span>
            </div>
          </div>
          <div style={styles.headerActions}>
            <button
              type="button"
              className="cbid-icon-btn"
              style={styles.iconBtn}
              onClick={billNoAPI}
              title="Refresh"
            >
              <FiRefreshCw size={15} />
            </button>
            <button
              type="button"
              className="cbid-icon-btn"
              style={styles.iconBtn}
              onClick={() => {
                setMobileOpen(false);
                smCodeRef.current?.focus();
              }}
              title="Close"
            >
              <FiX size={16} />
            </button>
          </div>
        </div>
      }
    >
      <div style={styles.body}>
        <div style={styles.filters}>
          <div className="cbid-search-pill" style={styles.searchPill}>
            <FiSearch style={styles.searchIcon} size={15} />
            <Input
              ref={searchInputRef}
              variant="borderless"
              placeholder="Search mobile number..."
              value={mobile}
              maxLength={10}
              onChange={(e) => {
                const value = e.target.value.replace(/[a-zA-Z]/g, "");
                if (value.length <= 10) {
                  setMobile(value);
                }
              }}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => handleKeyDown(e, submitBillNoRef)}
              style={styles.searchInput}
            />
          </div>

          <Button
            ref={submitBillNoRef}
            className="cbid-submit-btn"
            style={styles.submitBtn}
            onClick={billNoAPI}
            loading={loading}
          >
            Submit
          </Button>

          <div style={styles.totalStat}>
            <span style={styles.totalLabel}>Total Amount</span>
            <span style={styles.totalValue}>
              &#8377;{Number(totalAmount || 0).toFixed(0)}
            </span>
          </div>
        </div>

        <div style={styles.tableCard}>
          <Table
            dataSource={billData}
            columns={columns3}
            size="small"
            className="cbid-table"
            rowKey={(record, index) => record.billno ?? index}
            loading={loading}
            pagination={false}
            scroll={{ y: 300 }}
            locale={{
              emptyText: searched ? (
                <div style={styles.empty}>
                  <span style={styles.emptyTitle}>No invoices found</span>
                  <span style={styles.emptySub}>
                    Try a different mobile number.
                  </span>
                </div>
              ) : (
                <div style={styles.empty}>
                  <span style={styles.emptyTitle}>
                    No search performed yet
                  </span>
                  <span style={styles.emptySub}>
                    Enter a mobile number and press Submit.
                  </span>
                </div>
              ),
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

export default CustomerBillInfoDialog;